import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as correctiveActionActions from '../../../store/actions/corrective-action.actions';
import { MessageService } from 'primeng/api';
import { CorrectiveAction } from '../../../models/corrective-action.model';

import {
  selectCorrectiveAction,
  selectCorrectiveActionError,
  selectCorrectiveActionLoading,
} from '../../../store/selectors/corrective-action.selector';
import { GLOBAL_TOAST } from '../../../shared/constants/global-key.constans';

@Component({
  selector: 'app-corrective-actions',
  templateUrl: './corrective-actions.component.html',
  styleUrl: './corrective-actions.component.scss',
  standalone: false,
})
export class CorrectiveActionsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() ruleUid: string | undefined = undefined;

  correctiveActions: CorrectiveAction[] | undefined = undefined;
  private destroy$ = new Subject<void>();
  loading$ = this.store.select(selectCorrectiveActionLoading);
  error$ = this.store.select(selectCorrectiveActionError);

  // Modal properties
  showEditModal = false;

  get sortedCorrectiveActions(): CorrectiveAction[] {
    if (!this.correctiveActions) {
      return [];
    }

    return [...this.correctiveActions].sort((a, b) => {
      return a.description.localeCompare(b.description);
    });
  }

  constructor(
    private store: Store,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ruleUid']) {
      const newRuleUid = changes['ruleUid'].currentValue;
      if (newRuleUid) {
        this.ruleUid = newRuleUid;
        this.loadInitialData();
      }
    }
  }

  ngOnInit(): void {
    this.subscribeToStore();
  }

  private loadInitialData(): void {
    this.store.dispatch(
      correctiveActionActions.loadCorrectiveAction({ ruleUid: this.ruleUid }),
    );
  }

  private subscribeToStore(): void {
    combineLatest([this.store.select(selectCorrectiveAction)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([correctiveActions]) => {
        this.correctiveActions = correctiveActions;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openEditModal(): void {
    this.showEditModal = true;
  }

  onModalSubmit(result: any): void {
    this.handleModalResult(result);
    this.showEditModal = false;
  }

  onModalCancel(): void {
    this.showEditModal = false;
  }

  private async handleModalResult(result: any): Promise<void> {
    const { toCreate, toUpdate, toDelete } = result;

    // Dispatch actions to the store
    if (toDelete && toDelete.length > 0) {
      this.store.dispatch(
        correctiveActionActions.deleteMultipleCorrectiveActions({
          uids: toDelete,
        }),
      );
    }

    if (toUpdate && toUpdate.length > 0) {
      const updateRequests = toUpdate.map((action: any) => ({
        uid: action.uid,
        description: action.description,
        ruleFK: this.ruleUid!,
      }));
      this.store.dispatch(
        correctiveActionActions.updateMultipleCorrectiveActions({
          requests: updateRequests,
        }),
      );
    }

    if (toCreate && toCreate.length > 0) {
      const createRequests = toCreate.map((action: any) => ({
        description: action.description,
        ruleFK: this.ruleUid!,
      }));
      this.store.dispatch(
        correctiveActionActions.createMultipleCorrectiveActions({
          requests: createRequests,
        }),
      );
    }

    // Only show success message if there were actual changes
    const hasChanges =
      (toCreate && toCreate.length > 0) ||
      (toUpdate && toUpdate.length > 0) ||
      (toDelete && toDelete.length > 0);

    if (hasChanges) {
      this.messageService.add({
        key: GLOBAL_TOAST,
        severity: 'success',
        summary: 'Succès',
        detail: 'Les modifications ont été envoyées.',
      });
    } else {
      console.log('No changes detected, skipping API calls');
    }
  }
}
